import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Empty, Spin, Typography } from 'antd';
import { QRCodeCanvas } from 'qrcode.react';
import s from './style.m.less';
import loginSuccessIcon from '@/assets/images/login/login_success_icon.webp';

/* ---------- types ---------- */
interface QrTokenResult {
    token?: string;
    expire_seconds?: number;
    [key: string]: unknown;
}

interface QrStatusResult {
    status?: string;
    code?: string;
    login_name?: string;
    token_value?: string;
    [key: string]: unknown;
}

interface LoginStore {
    tenantInfo?: { id?: string; [key: string]: unknown };
    //eslint-disable-next-line
    fetchQrLoginToken: (params: Record<string, unknown>) => Promise<QrTokenResult | null>;
    //eslint-disable-next-line
    checkQrLoginStatus: (params: { token: string }) => Promise<QrStatusResult | null>;
    //eslint-disable-next-line
    handleQrLoginSuccess: (data: Record<string, unknown>) => Promise<void>;
}

interface StoreProp {
    login: LoginStore;
    [key: string]: unknown;
}

/* ---------- constants ---------- */
const POLL_INTERVAL_MS = 3000,

 getQrToken = (data: QrTokenResult | null | undefined): string =>
    (data as QrTokenResult)?.token || '',

 getStatus = (data: QrStatusResult | null | undefined): string =>
    String((data as QrStatusResult)?.status || (data as QrStatusResult)?.code || '').toLowerCase(),

/* ---------- component ---------- */
 QrAssistLogin: React.FC<{ store?: StoreProp }> = ({ store }) => {
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null),
     unmountedRef = useRef(false),
     scannedRef = useRef(false),
     [loading, setLoading] = useState(false),
     [expired, setExpired] = useState(false),
     [qrText, setQrText] = useState(''),
     [statusText, setStatusText] = useState('请使用 APP 扫一扫登录'),
     [scanUserName, setScanUserName] = useState(''),
     [success, setSuccess] = useState(false),
     [scanned, setScanned] = useState(false),

     clearTimers = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []),

     loadQrCode = useCallback(async () => {
        clearTimers();
        setLoading(true);
        setExpired(false);
        setSuccess(false);
        setScanned(false);
        scannedRef.current = false;
        setScanUserName('');
        setStatusText('正在获取二维码...');

        try {
            const result = await store!.login.fetchQrLoginToken({
                tenant_id: store!.login.tenantInfo?.id || ''
            });
            if (unmountedRef.current) return;

            const token = getQrToken(result);

            if (!token) {
                setLoading(false);
                setExpired(true);
                setStatusText('二维码获取失败，请刷新重试');
                return;
            }

            setQrText(`https://guorentong/qr-login?token=${token}`);
            setLoading(false);
            setStatusText('请使用 APP 扫一扫登录');

            timerRef.current = setInterval(async () => {
                if (unmountedRef.current) return;
                try {
                    const res = await store!.login.checkQrLoginStatus({ token });
                    if (unmountedRef.current || !res) return;

                    const status = getStatus(res),
                     userName = (res as QrStatusResult)?.login_name || '';

                    if (status === 'pending' || status === 'c0101') {
                        setStatusText('请使用 APP 扫一扫登录');
                        setScanUserName('');
                        return;
                    }

                    if (status === 'scanned' || status === 'c0102') {
                        setStatusText('等待授权中...');
                        setScanUserName(userName);
                        setScanned(true);
                        scannedRef.current = true;
                        return;
                    }

                    if (status === 'cancelled' || status === 'c0104') {
                        setStatusText('扫码已取消，正在重新生成二维码');
                        setScanUserName('');
                        await loadQrCode();
                        return;
                    }

                    if (status === 'expired' || status === 'c0105') {
                        setStatusText('请使用 APP 扫一扫登录');
                        if (scannedRef.current) {
                            setScanUserName('');
                            await loadQrCode();
                            return;
                        }
                        clearTimers();
                        setExpired(true);
                        return;
                    }

                    if (status === 'confirmed' || status === 'c0103') {
                        clearTimers();
                        setSuccess(true);
                        setStatusText('正在进入系统...');
                        window.setTimeout(() => {
                            if (unmountedRef.current) return;
                            store!.login.handleQrLoginSuccess(
                                (res as QrStatusResult)?.token_value
                                    ? { token_value: res.token_value }
                                    : (res as Record<string, unknown>)
                            );
                        }, 1200);
                    }
                } catch (_error) {
                    // 保持当前二维码状态，下一轮继续轮询
                }
            }, POLL_INTERVAL_MS);
        } catch (_error) {
            if (unmountedRef.current) return;
            setLoading(false);
            setExpired(true);
            setStatusText('二维码获取失败，请刷新重试');
        }
    }, [clearTimers, store]);

    useEffect(() => {
        unmountedRef.current = false;
        loadQrCode();
        return () => {
            unmountedRef.current = true;
            clearTimers();
        };
    }, [clearTimers, loadQrCode]);

    return (
        <div>
            <div className={s.qr_header}>
                <div className={s.qr_title}>扫码登录</div>
            </div>
            {loading ? (
                <div className={s.qr_loading}>
                    <Spin />
                    <div className={s.qr_loading_text}>正在获取二维码...</div>
                </div>
            ) : !qrText ? (
                <div className={s.qr_empty}>
                    <Empty description="暂无二维码" />
                </div>
            ) : (
                <div className={s.qr_panel}>
                    <div className={`${s.qr_box} ${expired ? s.qr_box_expired : ''}`}>
                        {!expired && !success && !scanned ? (
                            <QRCodeCanvas value={qrText} size={140} />
                        ) : null}
                        {scanned || success || expired ? <div className={s.qr_mask}></div> : null}
                        {scanned && !success ? (
                            <div className={s.qr_state_card}>
                                <div className={s.qr_state_avatar}>
                                    {scanUserName ? scanUserName.slice(0, 1) + '*' : ''}
                                </div>
                                <div className={s.qr_state_title}>扫码成功</div>
                                <div className={s.qr_state_desc}>请在手机上确认登录</div>
                            </div>
                        ) : null}
                        {success ? (
                            <div className={s.qr_state_card}>
                                <img className={s.qr_state_success_icon} src={loginSuccessIcon} alt="登录成功" />
                                <div className={s.qr_state_title}>登录成功</div>
                            </div>
                        ) : null}
                        {expired ? (
                            <div className={s.qr_expired_state}>
                                <div className={s.qr_expired_title}>二维码已失效</div>
                                <div className={s.qr_expired_desc}>请点击刷新重新获取</div>
                                <Button type="primary" className={s.qr_refresh} onClick={loadQrCode}>
                                    刷新
                                </Button>
                            </div>
                        ) : null}
                    </div>
                    <Typography.Text className={s.qr_tip}>{statusText}</Typography.Text>
                </div>
            )}
        </div>
    );
};

export default inject('store')(observer(QrAssistLogin));
